import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { CONSENT_LANGS } from "../lib/helpers";
import { PageHeader, Field, NativeSelect, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

function TemplateEditor({ open, onOpenChange, tpl, onSaved }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [versions, setVersions] = useState({});
  const [active, setActive] = useState("en");
  useEffect(() => {
    if (!open) return;
    const v = tpl?.versions || { en: { title: "", body: "" } };
    setName(tpl?.name || ""); setVersions(v); setActive(Object.keys(v)[0]);
  }, [open, tpl]);
  const missing = Object.keys(CONSENT_LANGS).filter((l) => !versions[l]);
  const setV = (k, val) => setVersions({ ...versions, [active]: { ...versions[active], [k]: val } });
  const remove = (l) => { const v = { ...versions }; delete v[l]; setVersions(v); setActive(Object.keys(v)[0]); };

  const save = async () => {
    try {
      const body = { name, versions, active: tpl?.active ?? true };
      tpl?.id ? await api.put(`/consent-templates/${tpl.id}`, body) : await api.post("/consent-templates", body);
      toast.success("Consent form saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{t("consent_form")}</DialogTitle></DialogHeader>
        <Field label={t("name")}><Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <div className="flex flex-wrap items-center gap-2">
          {Object.keys(versions).map((l) => (
            <span key={l} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs cursor-pointer ${active === l ? "bg-primary text-primary-foreground" : ""}`}
              onClick={() => setActive(l)} data-testid={`template-lang-tab-${l}`}>
              {CONSENT_LANGS[l]}
              {Object.keys(versions).length > 1 && <X className="h-3 w-3" onClick={(e) => { e.stopPropagation(); remove(l); }} />}
            </span>
          ))}
          {missing.length > 0 && (
            <NativeSelect testId="template-add-lang" value="" className="h-8 text-xs" onChange={(l) => { if (l) { setVersions({ ...versions, [l]: { title: "", body: "" } }); setActive(l); } }}
              options={[{ value: "", label: "+ language" }, ...missing.map((l) => ({ value: l, label: CONSENT_LANGS[l] }))]} />
          )}
        </div>
        {versions[active] && (
          <div className="grid gap-3">
            <Field label={t("title")}><Input data-testid="template-title-input" value={versions[active].title} onChange={(e) => setV("title", e.target.value)} /></Field>
            <Field label={t("description")}><Textarea data-testid="template-body-input" rows={10} value={versions[active].body} onChange={(e) => setV("body", e.target.value)} /></Field>
          </div>
        )}
        <p className="text-xs text-muted-foreground">Saving creates a new version; already-signed forms keep the exact text that was signed.</p>
        <Button className="rounded-full" data-testid="template-save-button" disabled={!name} onClick={save}>{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Consents() {
  const { templates, reload } = useApp();
  const { t } = useT();
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <PageHeader title={t("consents")}><Button className="rounded-full" data-testid="new-template-btn" onClick={() => setEdit({})}>{t("create")}</Button></PageHeader>
      {!templates.length && <Empty text={t("no_data")} />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger">
        {templates.map((x) => (
          <button key={x.id} onClick={() => setEdit(x)} data-testid={`template-card-${x.id}`} className="lift text-left rounded-[var(--radius)] border bg-card p-6">
            <FileSignature className="h-5 w-5 text-primary mb-3" />
            <div className="font-display text-lg font-semibold">{x.name}</div>
            <div className="text-xs text-muted-foreground mt-1">v{x.version}</div>
            <div className="flex flex-wrap gap-1.5 mt-4">{Object.keys(x.versions).map((l) => <span key={l} className="rounded-full bg-muted px-2.5 py-0.5 text-xs uppercase">{l}</span>)}</div>
          </button>
        ))}
      </div>
      <TemplateEditor open={!!edit} onOpenChange={(o) => !o && setEdit(null)} tpl={edit} onSaved={reload} />
    </div>
  );
}
